import { useForm } from 'react-hook-form';
import { TextInput, Stack, Button } from '@mantine/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRef } from 'react';
import { env } from '../../utils/env';
import { trackEvent } from '../../utils/analytics';
import { AnalyticsEvent } from '../../utils/analytics';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';

interface EmailSignupFormProps {
  onSubmit: () => void;
  buttonText?: string;
  buttonFullWidth?: boolean;
}

interface FormValues {
  email: string;
}

export const EmailSignupForm = ({
  onSubmit,
  buttonText = 'Sign Up',
  buttonFullWidth = false,
}: EmailSignupFormProps) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  });

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const token = await recaptchaRef.current?.executeAsync();
      if (!token) {
        return;
      }
      const response = await api.post('/api/email/signup', {
        email: values.email,
        captchaToken: token,
      });

      if (response.status !== 200) {
        throw new Error('Failed to sign up');
      }

      trackEvent(AnalyticsEvent.EMAIL_LIST_SIGNUP_SUBMITTED, {
        email: values.email,
      });
      toast.showSuccess('Successfully joined email list');
      onSubmit();
    } catch (error) {
      console.error(error);
      toast.showError('Failed to join email list');
    } finally {
      recaptchaRef.current?.reset();
      reset();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack gap="xs">
        <TextInput
          label="Email"
          placeholder="your@email.com"
          error={errors.email?.message}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^\S+@\S+$/,
              message: 'Invalid email address',
            },
          })}
          className="plausible-event-name=Email-List-Signup-Input"
        />
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={env.RECAPTCHA_SITE_KEY || ''}
        />
        <Button
          variant="outline"
          color="brand"
          type="submit"
          fullWidth={buttonFullWidth}
          className="plausible-event-name=Email-List-Signup-Submit"
        >
          {buttonText}
        </Button>
      </Stack>
    </form>
  );
};
